import { Link } from "react-router-dom";
import { ExternalLink, Maximize2 } from "lucide-react";
import { StepScreenshot } from "@/components/walkthrough/step-screenshot";
import { PersonaVoice } from "@/components/ui/persona-voice";
import type { TargetBox } from "@/components/walkthrough/step-screenshot";
import type { ActiveStepInfo } from "./workspace-types";

interface WorkspaceFindingStepSectionProps {
  activeStep: ActiveStepInfo;
  targetBox?: TargetBox | null;
  targetTone?: string | null;
  highlights?: Array<{
    id: string;
    box: TargetBox;
    tone?: string | null;
    label?: string;
    isActive?: boolean;
  }>;
  onOpenZen?: () => void;
  onClose: () => void;
  onViewInTrace: () => void;
  walkthroughId: string;
  projectId: string;
}

export function WorkspaceFindingStepSection({
  activeStep,
  targetBox,
  targetTone,
  highlights,
  onOpenZen,
  onClose,
  onViewInTrace,
  walkthroughId,
  projectId,
}: WorkspaceFindingStepSectionProps) {
  return (
    <section className="space-y-2.5 border-t border-border/60 pt-4">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span className="font-medium text-foreground">
          Step {activeStep.stepNumber}: {activeStep.title}
        </span>
        <div className="flex items-center gap-3">
          {onOpenZen && (
            <button
              type="button"
              onClick={onOpenZen}
              className="inline-flex items-center gap-1 text-xs font-medium text-foreground hover:underline cursor-pointer"
              title="Inspect in Zen Mode Carousel"
            >
              <Maximize2 className="size-3 text-primary" />
              <span>Zen Mode</span>
            </button>
          )}
          <Link
            to={`/projects/${projectId}/walkthroughs/${activeStep.walkthroughId}?step=${activeStep.stepNumber}`}
            onClick={() => {
              if (activeStep.walkthroughId === walkthroughId) {
                onClose();
                onViewInTrace();
              }
            }}
            className="inline-flex items-center gap-1 text-xs hover:text-foreground transition-colors"
          >
            <span>View in trace</span>
            <ExternalLink className="size-3" aria-hidden="true" />
          </Link>
        </div>
      </div>

      {activeStep.personaReasoning && (
        <div className="rounded border border-border/60 bg-muted/20 px-3 py-2">
          <PersonaVoice size="sm">“{activeStep.personaReasoning}”</PersonaVoice>
        </div>
      )}

      <div className="aspect-video w-full overflow-hidden rounded border border-border bg-black/5 dark:bg-black/30">
        <StepScreenshot
          walkthroughId={activeStep.walkthroughId}
          stepNumber={activeStep.stepNumber}
          targetBox={targetBox}
          targetTone={targetTone}
          highlights={highlights}
          onOpenZoom={onOpenZen}
          alt={`Step ${activeStep.stepNumber} evidence`}
        />
      </div>

      {activeStep.pageUrl && (
        <div className="flex items-center justify-between gap-2 border-t border-border/40 pt-1 text-xs">
          <span className="text-muted-foreground shrink-0">Captured URL:</span>
          <a
            href={activeStep.pageUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 truncate font-mono text-xs text-foreground hover:underline"
            title={activeStep.pageUrl}
          >
            <span className="truncate">{activeStep.pageUrl}</span>
            <ExternalLink className="size-3 shrink-0 text-muted-foreground" />
          </a>
        </div>
      )}
    </section>
  );
}
